import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MessageSquare, Heart, Send, Trash2, Sparkles, Users, Trophy,
  ShieldCheck, Handshake, ArrowRight, X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CLUBS } from "@/data/clubs";
import BottomNav from "@/components/app/BottomNav";
import SafetyMenu from "@/components/app/SafetyMenu";
import { toast } from "sonner";

const clubColor = (n) => CLUBS.find(c => c.name === n)?.color || "#152A55";
const initialsOf = (name) => (name || "?").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();
const photoSrc = (url) => (url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);
const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), n = new Date();
  const s = Math.max(0, (n - d) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s/60)}m`;
  if (s < 86400) return `${Math.floor(s/3600)}h`;
  return `${Math.floor(s/86400)}d`;
};

const SCOPES = [
  { id: "all", label: "All", icon: Users },
  { id: "my_club", label: "My club", icon: Trophy },
  { id: "rivals", label: "Rivals", icon: Sparkles },
];

export default function Banter() {
  const { user } = useAuth();
  const myClub = user?.profile?.club || "";
  const [prompt, setPrompt] = useState(null);
  const [scope, setScope] = useState("all");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerText, setComposerText] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    api.get("/banter/prompt/current").then(r => setPrompt(r.data)).catch(() => {});
  }, []);

  const loadFeed = async (nextScope = scope) => {
    setLoading(true);
    try {
      const { data } = await api.get("/banter/feed", { params: { scope: nextScope } });
      setPosts(data.posts || []);
    } catch { toast.error("Could not load the banter feed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadFeed(scope); /* eslint-disable-next-line */ }, [scope]);

  const submitPost = async () => {
    const t = composerText.trim();
    if (!t) return;
    setPosting(true);
    try {
      const { data } = await api.post("/banter/posts", { text: t, prompt_id: prompt?.id });
      setPosts(p => [data, ...p]);
      setComposerText("");
      setComposerOpen(false);
      toast.success("Banter posted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not post");
    } finally { setPosting(false); }
  };

  const toggleLike = async (post) => {
    // Optimistic
    setPosts(list => list.map(p => p.id === post.id
      ? { ...p, liked_by_me: !p.liked_by_me, likes: p.likes + (p.liked_by_me ? -1 : 1) } : p));
    try { await api.post(`/banter/posts/${post.id}/like`); }
    catch { loadFeed(scope); }
  };

  const deletePost = async (post) => {
    if (!confirm("Delete this post?")) return;
    try {
      await api.delete(`/banter/posts/${post.id}`);
      setPosts(list => list.filter(p => p.id !== post.id));
      toast.success("Deleted");
    } catch { toast.error("Could not delete"); }
  };

  const onPostReplied = (postId, delta) => {
    setPosts(list => list.map(p => p.id === postId ? { ...p, replies: Math.max(0, p.replies + delta) } : p));
  };

  return (
    <div className="min-h-screen bg-footy-bg text-white pb-28 grain">
      <header className="sticky top-0 z-30 bg-footy-bg/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-md mx-auto flex items-center justify-between px-5 py-4">
          <h1 className="font-display text-3xl uppercase tracking-wider">Banter</h1>
          <button
            onClick={() => setComposerOpen(true)}
            className="w-10 h-10 rounded-full bg-footy-red flex items-center justify-center hover:bg-footy-redHover active:scale-90 transition"
            data-testid="banter-open-composer"
            aria-label="Post banter"
          >
            <Send size={17} />
          </button>
        </div>
        <div className="max-w-md mx-auto px-5 pb-3 flex gap-2" data-testid="banter-scope-tabs">
          {SCOPES.map(s => {
            const disabled = (s.id !== "all") && !myClub;
            return (
              <button
                key={s.id}
                onClick={() => !disabled && setScope(s.id)}
                disabled={disabled}
                data-testid={`banter-scope-${s.id}`}
                className={`flex-1 py-2 rounded-full text-xs font-bold uppercase tracking-widest border transition-colors inline-flex items-center justify-center gap-2 ${
                  scope === s.id
                    ? "bg-footy-red border-footy-red text-white"
                    : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                }`}
              >
                <s.icon size={12} /> {s.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="max-w-md mx-auto px-5 pt-5" data-testid="banter-feed">
        {prompt && (
          <WeeklyPromptCard
            prompt={prompt}
            onOpenComposer={() => setComposerOpen(true)}
          />
        )}

        {loading ? (
          <div className="space-y-3 mt-4">
            {[1,2,3].map(i => <div key={i} className="h-40 rounded-2xl bg-white/5 animate-pulse" />)}
          </div>
        ) : posts.length === 0 ? (
          <EmptyState scope={scope} onCompose={() => setComposerOpen(true)} />
        ) : (
          <div className="space-y-3 mt-4">
            {posts.map(p => (
              <BanterCard
                key={p.id}
                post={p}
                meId={user?.id}
                onToggleLike={() => toggleLike(p)}
                onDelete={() => deletePost(p)}
                onReplyDelta={(d) => onPostReplied(p.id, d)}
              />
            ))}
          </div>
        )}
      </div>

      {composerOpen && (
        <ComposerModal
          prompt={prompt}
          text={composerText}
          setText={setComposerText}
          posting={posting}
          onClose={() => setComposerOpen(false)}
          onSubmit={submitPost}
        />
      )}

      <BottomNav />
    </div>
  );
}


function WeeklyPromptCard({ prompt, onOpenComposer }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-footy-red/40 bg-gradient-to-br from-footy-red/[0.12] to-transparent p-5"
      data-testid="banter-weekly-prompt"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={14} className="text-footy-red" />
        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-footy-red">This week&apos;s prompt</span>
      </div>
      <p className="font-display text-2xl uppercase leading-tight mt-1" data-testid="banter-prompt-text">
        {prompt.text}
      </p>
      <button
        onClick={onOpenComposer}
        className="footy-btn-primary !py-2 !px-4 !text-xs mt-4"
        data-testid="banter-prompt-answer-btn"
      >
        <Send size={12} /> Answer it
      </button>
    </div>
  );
}


function EmptyState({ scope, onCompose }) {
  const nav = useNavigate();
  return (
    <div className="footy-card p-7 text-center mt-4" data-testid="banter-empty">
      <MessageSquare className="mx-auto text-footy-red mb-3" />
      <h3 className="font-display text-2xl uppercase">
        {scope === "my_club" ? "No banter from your club yet" : scope === "rivals" ? "Rivals are quiet — for once" : "Kick off the banter"}
      </h3>
      <p className="text-sm text-slate-400 mt-2">
        Post your take. Anyone who replies is fair game to match with.
      </p>
      <button onClick={onCompose} className="footy-btn-primary mt-5" data-testid="banter-empty-cta">
        <Send size={14} /> Post banter
      </button>
      <div className="mt-4 text-[11px] text-slate-500">
        Not feeling chatty?{" "}
        <button onClick={() => nav("/discover")} className="text-footy-red hover:underline" data-testid="banter-empty-discover">
          Swipe some fans instead →
        </button>
      </div>
    </div>
  );
}


function BanterCard({ post, meId, onToggleLike, onDelete, onReplyDelta }) {
  const nav = useNavigate();
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [replies, setReplies] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [matching, setMatching] = useState(false);
  const author = post.author || {};
  const color = clubColor(author.club);

  const loadReplies = async () => {
    try {
      const { data } = await api.get(`/banter/posts/${post.id}/replies`);
      setReplies(data.replies || []);
    } catch { toast.error("Could not load replies"); }
  };
  const toggleReplies = async () => {
    const next = !repliesOpen;
    setRepliesOpen(next);
    if (next && replies === null) await loadReplies();
  };
  const sendReply = async () => {
    const t = replyText.trim();
    if (!t) return;
    setSendingReply(true);
    try {
      const { data } = await api.post(`/banter/posts/${post.id}/replies`, { text: t });
      setReplies(r => [...(r || []), data]);
      setReplyText("");
      onReplyDelta(+1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reply");
    } finally { setSendingReply(false); }
  };
  const deleteReply = async (id) => {
    if (!confirm("Delete this reply?")) return;
    try {
      await api.delete(`/banter/replies/${id}`);
      setReplies(r => (r || []).filter(x => x.id !== id));
      onReplyDelta(-1);
    } catch { toast.error("Could not delete"); }
  };

  // "Match with me" — instant mutual like using the existing /swipe endpoint.
  // Fires from the primary CTA on each card AND from the small avatar-tap.
  const connect = async () => {
    if (!author?.id || author.id === meId) return;
    setMatching(true);
    try {
      const { data } = await api.post("/swipe", { target_id: author.id, action: "like" });
      if (data?.match?.id) {
        toast.success("Match! Opening your chat…");
        nav(`/chat/${data.match.id}`);
      } else {
        toast.success(`You liked ${author.name}. If they like back, it's a match.`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not connect");
    } finally { setMatching(false); }
  };

  return (
    <article
      className="footy-card overflow-hidden"
      data-testid={`banter-post-${post.id}`}
    >
      <header className="flex items-center gap-3 p-4">
        <button
          type="button"
          onClick={connect}
          disabled={matching || post.mine}
          className="w-11 h-11 rounded-full border-2 flex-none overflow-hidden flex items-center justify-center"
          style={{ borderColor: color }}
          data-testid={`banter-avatar-${post.id}`}
          title={post.mine ? "Your post" : `Match with ${author.name}`}
        >
          {author.photo_url ? (
            <img src={photoSrc(author.photo_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-display text-base"
                 style={{ background: `linear-gradient(160deg, ${color}, #061022)` }}>
              {initialsOf(author.name)}
            </div>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-bold leading-tight flex items-center gap-2">
            <span className="truncate">{author.name || "Someone"}</span>
            {author.photo_verified && (
              <span title="Verified Supporter" className="text-emerald-300 flex-none">
                <ShieldCheck size={11} strokeWidth={3} />
              </span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            {author.club || "—"} · {timeAgo(post.created_at)}
          </div>
        </div>
        {post.mine ? (
          <button onClick={onDelete} className="p-2 text-slate-500 hover:text-footy-red"
            aria-label="Delete post" data-testid={`banter-delete-${post.id}`}>
            <Trash2 size={14} />
          </button>
        ) : author?.id ? (
          <SafetyMenu
            targetId={author.id}
            targetName={author.name}
            context="banter"
            variant="header"
          />
        ) : null}
      </header>

      <div className="px-4 pb-3">
        <p className="text-[15px] leading-snug whitespace-pre-wrap" data-testid={`banter-text-${post.id}`}>
          {post.text}
        </p>
      </div>

      <div className="px-4 pb-3 flex items-center gap-2 text-xs text-slate-400">
        <button onClick={onToggleLike} data-testid={`banter-like-${post.id}`}
          className={`inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full border transition-colors ${
            post.liked_by_me ? "border-footy-red bg-footy-red/15 text-footy-red" : "border-white/10 hover:border-white/25"
          }`}>
          <Heart size={12} fill={post.liked_by_me ? "currentColor" : "none"} /> {post.likes}
        </button>
        <button onClick={toggleReplies} data-testid={`banter-reply-toggle-${post.id}`}
          className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full border border-white/10 hover:border-white/25">
          <MessageSquare size={12} /> {post.replies}
        </button>
      </div>

      {/* CORE DIFFERENTIATOR: prominent "Match with me" CTA on every non-own post.
          Prevents the surface from devolving into a passive banter feed. */}
      {!post.mine && author?.id && (
        <div className="px-4 pb-4">
          <button
            onClick={connect}
            disabled={matching}
            className="w-full py-2.5 rounded-full text-xs font-bold uppercase tracking-widest bg-footy-red hover:bg-footy-redHover text-white inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid={`banter-match-${post.id}`}
          >
            <Handshake size={13} /> {matching ? "Sending…" : `Match with ${author.name?.split(" ")[0] || "them"}`}
            <ArrowRight size={12} />
          </button>
        </div>
      )}

      {repliesOpen && (
        <div className="border-t border-white/10 bg-white/[0.02]" data-testid={`banter-replies-${post.id}`}>
          <div className="px-4 py-3 space-y-3">
            {replies === null ? (
              <div className="text-center text-slate-500 text-xs py-2">Loading…</div>
            ) : replies.length === 0 ? (
              <p className="text-center text-slate-500 text-xs py-2">No replies yet. Be first — you&apos;ll skip the swipe queue.</p>
            ) : (
              replies.map(r => (
                <ReplyRow key={r.id} reply={r} meId={meId} onDelete={() => deleteReply(r.id)} onConnect={connect} />
              ))
            )}
          </div>
          <div className="px-3 pb-3 flex gap-2 items-center">
            <input
              value={replyText}
              onChange={(e) => setReplyText(e.target.value.slice(0, 280))}
              placeholder="Add your take…"
              maxLength={280}
              className="footy-input flex-1 !py-2 !text-sm !rounded-full"
              data-testid={`banter-reply-input-${post.id}`}
            />
            <button
              onClick={sendReply}
              disabled={sendingReply || !replyText.trim()}
              className="w-10 h-10 rounded-full bg-footy-red hover:bg-footy-redHover text-white flex items-center justify-center disabled:opacity-40 active:scale-90 transition"
              data-testid={`banter-reply-send-${post.id}`}
              aria-label="Send reply"
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </article>
  );
}


function ReplyRow({ reply, meId, onDelete, onConnect }) {
  const author = reply.author || {};
  const color = clubColor(author.club);
  const mine = reply.mine;
  return (
    <div className="flex items-start gap-2.5" data-testid={`banter-reply-${reply.id}`}>
      <button
        type="button"
        onClick={mine ? undefined : onConnect}
        className="w-8 h-8 rounded-full border-2 flex-none overflow-hidden flex items-center justify-center"
        style={{ borderColor: color }}
        title={mine ? "You" : `Match with ${author.name}`}
      >
        {author.photo_url ? (
          <img src={photoSrc(author.photo_url)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[11px] font-display"
               style={{ background: `linear-gradient(160deg, ${color}, #061022)` }}>
            {initialsOf(author.name)}
          </div>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5">
          <span className="font-bold text-xs">{author.name || "Someone"}</span>
          <span className="text-[10px] uppercase tracking-widest text-slate-500">{author.club || "—"}</span>
          <span className="text-[10px] text-slate-500 ml-auto">{timeAgo(reply.created_at)}</span>
        </div>
        <p className="text-[13px] leading-snug text-slate-200 mt-0.5 whitespace-pre-wrap">{reply.text}</p>
      </div>
      {mine && (
        <button onClick={onDelete} className="text-slate-500 hover:text-footy-red p-1" aria-label="Delete reply"
          data-testid={`banter-reply-delete-${reply.id}`}>
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}


function ComposerModal({ prompt, text, setText, posting, onClose, onSubmit }) {
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-footy-bg/85 backdrop-blur p-0 sm:p-6"
      onClick={onClose}
      data-testid="banter-composer-modal"
    >
      <div
        className="w-full max-w-md bg-footy-card border border-white/10 sm:rounded-2xl rounded-t-2xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-footy-red font-bold">Post banter</p>
            <h2 className="font-display text-2xl uppercase mt-1 leading-tight">Fire away</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        {prompt && (
          <div className="mb-3 p-3 rounded-xl bg-footy-red/10 border border-footy-red/30 text-xs">
            <div className="flex items-center gap-1.5 text-footy-red font-bold uppercase tracking-widest text-[10px] mb-1">
              <Sparkles size={11} /> This week
            </div>
            <p className="text-white leading-snug">{prompt.text}</p>
          </div>
        )}
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 280))}
          rows={5}
          placeholder="Your hot take…"
          className="footy-input resize-none w-full !text-[15px]"
          data-testid="banter-composer-input"
        />
        <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
          <span>Keep it banter, not abuse.</span>
          <span className={text.length > 260 ? "text-footy-red font-bold" : ""}>{text.length}/280</span>
        </div>
        <button
          onClick={onSubmit}
          disabled={posting || !text.trim()}
          className="footy-btn-primary w-full mt-4 disabled:opacity-50"
          data-testid="banter-composer-submit"
        >
          <Send size={14} /> {posting ? "Posting…" : "Post banter"}
        </button>
      </div>
    </div>
  );
}
